import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, Gift, Star, Calendar, CreditCard, Shield, ChevronRight, 
  Sparkles, Package, ShoppingBag, Code, TrendingUp, BookOpen, Video,
  Award, Users, Smartphone, Monitor, FileText, Headphones, ShoppingCart,
  Shirt, Coffee, Package2, Palette, Clock, Zap, BarChart, Download,
  Globe, DollarSign, MessageSquare, Youtube, Mail, Image, Timer,
  TreePine, Heart, Ghost, Gem, AlertCircle, Play, TrendingDown,
  Building, UserPlus, FolderOpen, Eye, FileText as Report, Settings,
  BarChart3, Plus, Lock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TeamMembers from '../components/Agency/TeamMembers';
import Clients from '../components/Agency/Clients';
import LicenseUpgradeModal from '../components/LicenseUpgradeModal';
import Button from '../components/Button';
import { API_ENDPOINTS } from '../config/api';
import { hasAgencyAccess } from '../utils/licensing';

type AgencyTab = 'overview' | 'team-members' | 'clients' | 'analytics';

interface AgencyStats {
  teamMembers: number;
  activeClients: number;
  mockupsCreated: number;
  totalRevenue: number;
}

const Agency: React.FC = () => {
  const navigate = useNavigate();
  const { profile, session } = useAuth();
  const [activeTab, setActiveTab] = useState<AgencyTab>('overview');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [stats, setStats] = useState<AgencyStats>({
    teamMembers: 0,
    activeClients: 0,
    mockupsCreated: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200); // 20 minutes countdown
  const [showUrgencyBanner, setShowUrgencyBanner] = useState(false);
  const [spotsLeft, setSpotsLeft] = useState(23); // Starting spots

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 180 && !showUrgencyBanner) { // Show urgency at 3 minutes
          setShowUrgencyBanner(true);
        }
        return prevTime > 0 ? prevTime - 1 : 0;
      });
    }, 1000);

    // Simulate spots being taken
    const spotsTimer = setInterval(() => {
      setSpotsLeft((prev) => {
        const decrement = Math.random() > 0.8 ? 1 : 0;
        return prev > 5 ? prev - decrement : prev;
      });
    }, 20000); // Every 20 seconds

    return () => {
      clearInterval(timer);
      clearInterval(spotsTimer);
    };
  }, [showUrgencyBanner]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUpgrade = async () => {
    setIsProcessing(true);
    try {
      // TODO: Implement actual upgrade logic with payment processing
      console.log('Processing Agency License upgrade...');
      // Redirect to payment processor or handle upgrade
    } catch (error) {
      console.error('Upgrade error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNoThanks = () => {
    // Redirect to dashboard
    navigate('/dashboard');
  };
  
  const hasAgencyLicense = hasAgencyAccess(profile);

  // Fetch agency statistics
  const fetchStats = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.AGENCY.STATS, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.stats) {
          setStats({
            teamMembers: data.stats.team_members || 0,
            activeClients: data.stats.active_clients || 0,
            mockupsCreated: data.stats.mockups_created || 0,
            totalRevenue: data.stats.total_revenue || 0
          });
        }
      } else {
        console.error('Failed to fetch agency stats');
      }
    } catch (error) {
      console.error('Error fetching agency stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasAgencyLicense && session?.access_token) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [hasAgencyLicense, session?.access_token]);

  // If user doesn't have agency license, show upgrade page
  if (!hasAgencyLicense) {
    return (
      <>
        {/* Google Fonts */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Kalam:wght@400;700&display=swap" rel="stylesheet" />
        
        <style>{`
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes slide {
            from { transform: translateX(0); }
            to { transform: translateX(20px); }
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0) rotate(-2deg); }
            50% { transform: translateY(-10px) rotate(2deg); }
          }
          
          .animate-spin-slow {
            animation: spin-slow 3s linear infinite;
          }
          
          .animate-fadeIn {
            animation: fadeIn 0.8s ease-out;
          }
          
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          
          .animation-delay-500 {
            animation-delay: 0.5s;
          }
          
          .animation-delay-1000 {
            animation-delay: 1s;
          }
        `}</style>
        
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Urgency Banner - Shows when time is low */}
        {showUrgencyBanner && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 px-4 shadow-lg animate-pulse">
            <div className="max-w-6xl mx-auto flex items-center justify-center gap-4 text-sm md:text-base font-bold">
              <AlertCircle className="w-5 h-5" />
              <span>⚠️ FINAL WARNING: Agency License expires in {formatTime(timeLeft)} - Last chance!</span>
              <AlertCircle className="w-5 h-5" />
            </div>
          </div>
        )}

        {/* Scarcity Bar - Dark Mode Design */}
        <div className="relative bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white py-4 px-4 overflow-hidden shadow-2xl border-b border-gray-700">
          {/* Animated background effect */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 via-orange-600/30 to-red-600/20"></div>
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 10px,
                  rgba(255, 255, 255, 0.03) 10px,
                  rgba(255, 255, 255, 0.03) 20px
                )`,
                animation: 'slide 20s linear infinite'
              }}
            ></div>
          </div>
          
          {/* Enhanced flashing lights effect */}
          <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
            <div className="absolute top-2 left-10 w-3 h-3 bg-red-400 rounded-full animate-ping shadow-lg"></div>
            <div className="absolute top-3 right-20 w-2 h-2 bg-orange-400 rounded-full animate-ping animation-delay-500 shadow-lg"></div>
            <div className="absolute bottom-2 left-1/3 w-2 h-2 bg-yellow-400 rounded-full animate-ping animation-delay-1000 shadow-lg"></div>
            <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-red-500 rounded-full animate-pulse animation-delay-1500"></div>
          </div>
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Spots Counter - Enhanced */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-orange-600 rounded-lg blur opacity-75 animate-pulse"></div>
                  <div className="relative flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 px-5 py-2.5 rounded-lg border border-yellow-400/50 shadow-lg">
                    <div className="flex items-center gap-1">
                      <TrendingDown className="w-5 h-5 text-yellow-300 animate-bounce" />
                      <AlertCircle className="w-4 h-4 text-yellow-300 animate-pulse" />
                    </div>
                    <span className="font-black text-lg text-white drop-shadow-lg">
                      ONLY {spotsLeft} AGENCY LICENSES LEFT
                    </span>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 text-sm text-yellow-200 font-medium">
                  <Sparkles className="w-4 h-4" />
                  <span>final invitation</span>
                </div>
              </div>

              {/* Progress Bar - Enhanced */}
              <div className="flex-1 max-w-sm">
                <div className="relative">
                  <div className="bg-black/50 rounded-full h-4 overflow-hidden shadow-inner">
                    <div 
                      className="relative h-full transition-all duration-500 ease-out rounded-full"
                      style={{ 
                        width: `${((50 - spotsLeft) / 50) * 100}%`,
                        background: `linear-gradient(to right, #10b981 0%, #eab308 ${((50 - spotsLeft) / 50) * 50}%, #ef4444 100%)`
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-transparent to-white/30"></div>
                      <div className="absolute right-0 top-0 bottom-0 w-2 bg-white/50 animate-pulse"></div>
                    </div>
                  </div>
                  <p className="text-xs text-yellow-200 mt-1 text-center font-medium">
                    🔥 {50 - spotsLeft} agencies already activated!
                  </p>
                </div>
              </div>

              {/* Timer - Enhanced */}
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg blur opacity-75 animate-pulse"></div>
                <div className="relative flex items-center gap-2 bg-gradient-to-r from-yellow-600 to-orange-600 px-5 py-2.5 rounded-lg border border-yellow-300/50 shadow-lg">
                  <Clock className="w-5 h-5 text-white animate-spin-slow drop-shadow" />
                  <div>
                    <p className="text-xs text-yellow-100 font-medium">FINAL OFFER EXPIRES</p>
                    <span className="font-black text-xl text-white tabular-nums drop-shadow-lg">
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showUpgradeModal && (
          <LicenseUpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            profile={profile}
          />
        )}
      </div>
      </>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'team-members', name: 'Team Members', icon: Users },
    { id: 'clients', name: 'Clients', icon: Building },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
  ] as const;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Agency Dashboard</h1>
                <p className="text-gray-600 mt-1">
                  Manage your team, clients, and agency operations
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={() => setActiveTab('team-members')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Team Member
                </Button>
                
                <Button
                  variant="primary"
                  size="medium"
                  onClick={() => setActiveTab('clients')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </div>
            </div>
            
            {/* Tab Navigation */}
            <div className="mt-6">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? 'border-brand-purple text-brand-purple'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4 inline mr-2" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-pulse">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                        <div className="h-8 bg-gray-200 rounded w-12"></div>
                      </div>
                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Team Members</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.teamMembers}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Active Clients</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.activeClients}</p>
                    </div>
                    <Building className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Mockups Created</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.mockupsCreated}</p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                      <p className="text-2xl font-bold text-gray-900">${stats.totalRevenue.toFixed(2)}</p>
                    </div>
                    <CreditCard className="w-8 h-8 text-orange-600" />
                  </div>
                </div>
              </div>
            )}
            
            {/* Recent Activity Placeholder */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No recent activity to show</p>
                <p className="text-gray-400 text-sm mt-2">
                  Activity will appear here as your team creates mockups and manages clients
                </p>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'team-members' && <TeamMembers />}
        {activeTab === 'clients' && <Clients />}
        {activeTab === 'analytics' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Analytics Dashboard</h3>
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Analytics features coming soon</p>
              <p className="text-gray-400 text-sm mt-2">
                Track team performance, client satisfaction, and revenue insights
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Agency;
